import React, { useCallback, useState } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Button from 'fe-design-base/molecules/Button/Button';
import Divider from 'fe-design-base/molecules/Divider/Divider';
import PhoneField from 'fe-design-base/molecules/PhoneField';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { sendSmsOtp } from 'features/authentication/actions';
import {
  GOOGLE_SIGN_IN_PATH,
  PhoneFormStep,
  SignInVariant,
} from 'features/authentication/SignIn/constants';
import HeaderLayout from 'features/authentication/SignIn/HeaderLayout';
import { ErrorTrackingProps } from 'features/authentication/SignIn/types';

import { toI18n } from 'util/i18n';

import SignUpSection from '../SignUpSection';

interface FormValues {
  phone: string;
}

type Props = {
  onSetStep: (step: PhoneFormStep) => void;
  onSetPhoneNumber: (phone: string) => void;
  onSetPhoneId: (methodId: string) => void;
  onChangeSignInType: (view: SignInVariant) => void;
} & ErrorTrackingProps;

const validationSchema = Yup.object().shape({
  phone: Yup.string()
    .required(toI18n('fe_design_base.errors.field.phone_required'))
    .matches(/^\d{10}$/, toI18n('fe_design_base.errors.field.phone_invalid')),
});

const PhoneForm = ({
  onSetStep,
  onSetPhoneNumber,
  onSetPhoneId,
  onChangeSignInType,
  onError,
}: Props) => {
  const [alertError, setAlertError] = useState('');

  const handleEmailSignInClick = useCallback(() => {
    onChangeSignInType(SignInVariant.EMAIL);
  }, [onChangeSignInType]);

  const handleError = useCallback(
    // TODO: https://joinhomebase.atlassian.net/browse/FE-2199
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    error => {
      let errorMessage = toI18n('fe_design_base.errors.general');
      if (error?.includes('MFA is enabled')) {
        errorMessage = toI18n('authentication.errors.phone_signin_mfa_enabled');
      }

      setAlertError(errorMessage);
      onError(errorMessage);
    },
    [onError]
  );

  const handleSubmit = useCallback(
    ({ phone }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      onSetPhoneNumber(phone);
      if (alertError) setAlertError('');

      sendSmsOtp({ phone })
        .then(({ phone_id }) => {
          onSetPhoneId(phone_id);
          onSetStep(PhoneFormStep.CODE);
        })
        .catch(e => {
          e.response.json().then((body: { errors?: string }) => {
            handleError(body.errors);
            setSubmitting(false);
          });
        });
    },
    [alertError, handleError, onSetPhoneId, onSetPhoneNumber, onSetStep]
  );

  return (
    <HeaderLayout
      title="authentication.sign_in"
      dataTestId="PhoneForm"
      alertError={alertError}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={{ phone: '' }}
        validationSchema={validationSchema}
      >
        {({ dirty, isValid, isSubmitting }) => (
          <Form role="form">
            <Box column>
              <Box column gap={16}>
                <PhoneField
                  name="phone"
                  fullWidth
                  dataTestId="PhoneForm__phone-input"
                  startIcon="Phone"
                />
                <Button
                  type="submit"
                  uxElement="send code button"
                  disabled={!dirty || !isValid}
                  isLoading={isSubmitting}
                >
                  {toI18n('authentication.sign_in')}
                </Button>
              </Box>
              <Divider spacing={32} color="mono500" />
            </Box>
          </Form>
        )}
      </Formik>
      <Box column gap={16} mb={32}>
        <Button
          variant="secondary"
          fullWidth
          startIcon="Email"
          uxElement="email sign in button"
          onClick={handleEmailSignInClick}
        >
          {toI18n('authentication.email_sign_in')}
        </Button>
        <Button
          variant="secondary"
          fullWidth
          startIcon="ColorGoogle"
          href={GOOGLE_SIGN_IN_PATH}
          uxElement="google sign in button"
        >
          {toI18n('authentication.google_sign_in')}
        </Button>
      </Box>
      <SignUpSection />
    </HeaderLayout>
  );
};

export default PhoneForm;
